import React, { useState } from 'react';
import { Box, Typography, Modal, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ContentPreviewModal from '../shared/ContentPreviewModal';

// Editorial theme colors
const COLORS = {
  cream: '#FAF8F5',
  charcoal: '#2D2A26',
  terracotta: '#C45D3A',
  border: '#2D2A26'
};

const SectionContainer = styled(Box)({
  backgroundColor: COLORS.cream,
  borderBottom: `1px solid ${COLORS.charcoal}`
});

const SectionHeader = styled(Box)({
  padding: '48px 64px 32px',
  borderBottom: `1px solid ${COLORS.charcoal}`,
  '@media (max-width: 768px)': {
    padding: '32px 20px 24px'
  }
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '42px',
  fontWeight: 700,
  color: COLORS.charcoal,
  letterSpacing: '-1px',
  '@media (max-width: 768px)': {
    fontSize: '32px'
  }
});

const SectionSubtitle = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: COLORS.charcoal,
  opacity: 0.6,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginTop: '8px'
});

const PostsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr'
  }
});

const PostCard = styled(Box)({
  borderRight: `1px solid ${COLORS.charcoal}`,
  borderBottom: `1px solid ${COLORS.charcoal}`,
  transition: 'background-color 0.3s ease',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    backgroundColor: `${COLORS.charcoal}08`
  },
  '&:nth-of-type(3n)': {
    borderRight: 'none',
    '@media (max-width: 1024px)': {
      borderRight: `1px solid ${COLORS.charcoal}`,
      '&:nth-of-type(2n)': {
        borderRight: 'none'
      }
    },
    '@media (max-width: 600px)': {
      borderRight: 'none'
    }
  },
  '@media (max-width: 1024px)': {
    '&:nth-of-type(2n)': {
      borderRight: 'none'
    }
  },
  '@media (max-width: 600px)': {
    borderRight: 'none'
  }
});

const PostImage = styled(Box)({
  height: '200px',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  borderBottom: `1px solid ${COLORS.charcoal}`,
  filter: 'grayscale(20%)',
  transition: 'filter 0.3s ease',
  cursor: 'pointer',
  position: 'relative',
  '&:hover': {
    filter: 'grayscale(0%)',
    '& .image-overlay': {
      opacity: 1
    }
  }
});

const ImageOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(45, 42, 38, 0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.3s ease'
});

const ViewText = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: COLORS.cream,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  padding: '10px 20px',
  border: `1px solid ${COLORS.cream}`
});

const LightboxModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(45, 42, 38, 0.95)'
});

const LightboxContent = styled(Box)({
  position: 'relative',
  maxWidth: '90vw',
  maxHeight: '90vh',
  outline: 'none'
});

const LightboxImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '90vh',
  objectFit: 'contain'
});

const CloseButton = styled(IconButton)({
  position: 'absolute',
  top: '-50px',
  right: '0',
  backgroundColor: COLORS.cream,
  color: COLORS.charcoal,
  borderRadius: 0,
  '&:hover': {
    backgroundColor: COLORS.terracotta,
    color: COLORS.cream
  }
});

const PostContent = styled(Box)({
  padding: '24px 32px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  '@media (max-width: 768px)': {
    padding: '20px'
  }
});

const PostType = styled(Box)({
  display: 'inline-block',
  padding: '6px 12px',
  border: `1px solid ${COLORS.charcoal}`,
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  color: COLORS.charcoal,
  marginBottom: '16px',
  alignSelf: 'flex-start'
});

const PostTypeOffer = styled(PostType)({
  backgroundColor: COLORS.terracotta,
  borderColor: COLORS.terracotta,
  color: COLORS.cream
});

const PostTypeEvent = styled(PostType)({
  backgroundColor: COLORS.charcoal,
  color: COLORS.cream
});

const PostSummary = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '15px',
  fontWeight: 400,
  color: COLORS.charcoal,
  lineHeight: 1.7,
  flex: 1,
  display: '-webkit-box',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  marginBottom: '16px'
});

const PostDate = styled(Typography)({
  fontFamily: "'Space Mono', monospace",
  fontSize: '12px',
  color: COLORS.charcoal,
  opacity: 0.5,
  letterSpacing: '1px',
  marginTop: 'auto'
});

const CTAButton = styled('a')({
  display: 'inline-block',
  padding: '12px 24px',
  backgroundColor: COLORS.charcoal,
  color: COLORS.cream,
  textDecoration: 'none',
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  marginTop: '16px',
  alignSelf: 'flex-start',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: COLORS.terracotta
  }
});

const getPostTypeLabel = (type) => {
  switch (type) {
    case 'EVENT':
      return 'Event';
    case 'OFFER':
      return 'Offer';
    case 'PRODUCT':
      return 'Product';
    case 'STANDARD':
      return 'Update';
    default:
      return 'Post';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).toUpperCase();
  } catch {
    return '';
  }
};

const getCTALabel = (actionType) => {
  switch (actionType) {
    case 'LEARN_MORE':
      return 'Learn More';
    case 'CALL':
      return 'Call Now';
    case 'BOOK':
      return 'Book Now';
    case 'ORDER':
      return 'Order Online';
    case 'SHOP':
      return 'Shop Now';
    case 'SIGN_UP':
      return 'Sign Up';
    default:
      return 'Learn More';
  }
};

const getPostTypeComponent = (type) => {
  switch (type) {
    case 'OFFER':
      return PostTypeOffer;
    case 'EVENT':
      return PostTypeEvent;
    default:
      return PostType;
  }
};

const EditorialGMBFeedSection = ({ gmbFeed }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);

  const {
    posts = [],
    showSection = true
  } = gmbFeed || {};

  if (!showSection || !posts || posts.length === 0) {
    return null;
  }

  const handleImageClick = (imageUrl, e) => {
    e.stopPropagation();
    setSelectedImage(imageUrl);
  };

  const handleClose = () => {
    setSelectedImage(null);
  };

  return (
    <SectionContainer id="updates">
      <SectionHeader>
        <SectionSubtitle>Stay Connected</SectionSubtitle>
        <SectionTitle>Latest Updates</SectionTitle>
      </SectionHeader>

      <PostsGrid>
        {posts.slice(0, 6).map((post, index) => {
          const imageUrl = post.media?.[0]?.googleUrl || post.media?.[0]?.sourceUrl || null;
          const hasImage = !!imageUrl;
          const TypeComponent = getPostTypeComponent(post.topicType);

          return (
            <PostCard key={post.id || index} onClick={() => setSelectedPost(post)}>
              {hasImage && (
                <PostImage
                  sx={{ backgroundImage: `url(${imageUrl})` }}
                  onClick={(e) => handleImageClick(imageUrl, e)}
                >
                  <ImageOverlay className="image-overlay">
                    <ViewText>View Image</ViewText>
                  </ImageOverlay>
                </PostImage>
              )}
              <PostContent>
                <TypeComponent>
                  {getPostTypeLabel(post.topicType)}
                </TypeComponent>

                <PostSummary>
                  {post.summary}
                </PostSummary>

                {post.callToAction && post.callToAction.url && (
                  <CTAButton
                    href={post.callToAction.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {getCTALabel(post.callToAction.actionType)}
                  </CTAButton>
                )}

                <PostDate>
                  {formatDate(post.createTime)}
                </PostDate>
              </PostContent>
            </PostCard>
          );
        })}
      </PostsGrid>

      <LightboxModal open={!!selectedImage} onClose={handleClose}>
        <LightboxContent>
          {selectedImage && (
            <>
              <CloseButton onClick={handleClose}>
                <CloseIcon />
              </CloseButton>
              <LightboxImage
                src={selectedImage}
                alt="Full size image"
              />
            </>
          )}
        </LightboxContent>
      </LightboxModal>

      <ContentPreviewModal
        open={!!selectedPost}
        onClose={() => setSelectedPost(null)}
        image={selectedPost?.media?.[0]?.googleUrl || selectedPost?.media?.[0]?.sourceUrl || null}
        imageAlt={selectedPost?.summary || 'GMB post'}
        label={selectedPost ? getPostTypeLabel(selectedPost.topicType) : ''}
        subtitle={selectedPost?.createTime ? formatDate(selectedPost.createTime) : ''}
        title={selectedPost?.title || selectedPost?.summary || 'Business Update'}
        description={selectedPost?.summary || selectedPost?.content || ''}
        ctaUrl={selectedPost?.callToAction?.url}
        ctaLabel={selectedPost?.callToAction ? getCTALabel(selectedPost.callToAction.actionType) : 'Learn More'}
        palette={{
          surface: COLORS.cream,
          text: COLORS.charcoal,
          muted: 'rgba(45, 42, 38, 0.72)',
          accent: COLORS.terracotta,
          border: 'rgba(45, 42, 38, 0.12)',
          overlay: 'rgba(45, 42, 38, 0.9)',
          tagBg: 'rgba(196, 93, 58, 0.12)',
          tagText: COLORS.terracotta
        }}
      />
    </SectionContainer>
  );
};

export default EditorialGMBFeedSection;
